// CB:BEGIN admin-create-invoice-page
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SettingsCard from '../../components/ui/SettingsCard';

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Safe number from API (Prisma Decimal or number). */
function toNum(v: unknown): number | '' {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const n = Number(v);
  return Number.isNaN(n) ? '' : n;
}

interface InvoiceLineItemRow {
  id: string;
  category: string;
  description: string;
  quantity: number | '';
  unit: string;
  unitPriceCents: number | '';
  totalCents: number | '';
}

function defaultLineItem(): InvoiceLineItemRow {
  return {
    id: crypto.randomUUID(),
    category: '',
    description: '',
    quantity: '',
    unit: '',
    unitPriceCents: '',
    totalCents: '',
  };
}

interface AdminCreateInvoicePageProps {
  onNavigate?: (screen: string) => void;
}

export default function AdminCreateInvoicePage({ onNavigate }: AdminCreateInvoicePageProps) {
  const api = useApi();
  const [quoteNumberSearch, setQuoteNumberSearch] = useState('');
  const [recentQuotes, setRecentQuotes] = useState<Array<{
    id: string;
    quoteNumber: string;
    status: string;
    homeownerName: string | null;
    propertyAddress1: string;
    propertyCity: string;
    propertyState: string;
    createdAt: string;
    updatedAt: string;
  }>>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>('');
  const [quoteFindResult, setQuoteFindResult] = useState<{
    id: string;
    quoteNumber: string;
    homeownerName: string | null;
    propertyAddress1: string;
    propertyCity: string;
    propertyState: string;
    totalCents: number;
  } | null>(null);
  const [findingQuote, setFindingQuote] = useState(false);
  const [creatingFromQuote, setCreatingFromQuote] = useState(false);
  const [createFromQuoteDueDate, setCreateFromQuoteDueDate] = useState(() =>
    toDateStr(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))
  );
  const [createFromQuoteInvoiceDate, setCreateFromQuoteInvoiceDate] = useState(() => toDateStr(new Date()));
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    invoice: any;
    detected: 'corebids' | 'external';
    quoteNumber?: string;
    warnings: string[];
  } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<any | null>(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [homeownerName, setHomeownerName] = useState('');
  const [homeownerEmail, setHomeownerEmail] = useState('');
  const [homeownerPhone, setHomeownerPhone] = useState('');
  const [propertyAddress1, setPropertyAddress1] = useState('');
  const [propertyAddress2, setPropertyAddress2] = useState('');
  const [propertyCity, setPropertyCity] = useState('');
  const [propertyState, setPropertyState] = useState('');
  const [propertyZip, setPropertyZip] = useState('');
  const [contractorBusinessName, setContractorBusinessName] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(toDateStr(new Date()));
  const [dueDate, setDueDate] = useState(toDateStr(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)));
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [lineItems, setLineItems] = useState<InvoiceLineItemRow[]>([defaultLineItem(), defaultLineItem()]);
  const [saving, setSaving] = useState(false);
  const [updatingDates, setUpdatingDates] = useState(false);
  const [renderingPdf, setRenderingPdf] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [changeOrdering, setChangeOrdering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastPdfUrl, setLastPdfUrl] = useState<string | null>(null);
  const [s3ConsoleUrl, setS3ConsoleUrl] = useState<string | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffData, setDiffData] = useState<any | null>(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const pdfOpenedRef = useRef<string | null>(null);

  const subtotalCents = lineItems.reduce((s, r) => s + (r.totalCents === '' ? 0 : Number(r.totalCents)), 0);
  const taxCents = Math.round(subtotalCents * 0.1);
  const totalCents = subtotalCents + taxCents;

  // Load recent quotes for dropdown once on mount (stable deps to avoid jumpy re-fetches)
  const apiRef = useRef(api);
  apiRef.current = api;
  useEffect(() => {
    let cancelled = false;
    setLoadingQuotes(true);
    apiRef.current
      .get<Array<{ id: string; quoteNumber: string; status: string; homeownerName: string | null; propertyAddress1: string; propertyCity: string; propertyState: string; createdAt: string; updatedAt: string }>>('/api/admin/quotes')
      .then((list) => {
        if (!cancelled && Array.isArray(list)) setRecentQuotes(list);
      })
      .catch(() => {
        if (!cancelled) setRecentQuotes([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingQuotes(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Load S3 console URL for "View bucket" link (admin only)
  useEffect(() => {
    let cancelled = false;
    apiRef.current
      .get<{ url?: string | null }>('/api/admin/invoices/storage-console-url')
      .then((data) => {
        if (!cancelled && data?.url) setS3ConsoleUrl(data.url);
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  const handleQuoteSelect = useCallback(
    async (quoteId: string) => {
      setSelectedQuoteId(quoteId);
      setQuoteFindResult(null);
      setError(null);
      if (!quoteId) return;
      setFindingQuote(true);
      try {
        const quote = await api.get<any>(`/api/admin/quotes/${quoteId}`);
        setQuoteFindResult({
          id: quote.id,
          quoteNumber: quote.quoteNumber,
          homeownerName: quote.homeownerName ?? null,
          propertyAddress1: quote.propertyAddress1 ?? '',
          propertyCity: quote.propertyCity ?? '',
          propertyState: quote.propertyState ?? '',
          totalCents: quote.totalCents ?? 0,
        });
      } catch (e: any) {
        setError(e.message || 'Failed to load quote');
      } finally {
        setFindingQuote(false);
      }
    },
    [api]
  );

  const loadInvoice = useCallback(
    async (id: string) => {
      setLoadingInvoice(true);
      setError(null);
      try {
        const data = await api.get<any>(`/api/admin/invoices/${id}`);
        setInvoice(data);
        setInvoiceId(data.id);
        setHomeownerName(data.homeownerName ?? '');
        setHomeownerEmail(data.homeownerEmail ?? '');
        setHomeownerPhone(data.homeownerPhone ?? '');
        setPropertyAddress1(data.propertyAddress1 ?? '');
        setPropertyAddress2(data.propertyAddress2 ?? '');
        setPropertyCity(data.propertyCity ?? '');
        setPropertyState(data.propertyState ?? '');
        setPropertyZip(data.propertyZip ?? '');
        setContractorBusinessName(data.contractorBusinessName ?? '');
        setInvoiceDate(data.invoiceDate ? toDateStr(new Date(data.invoiceDate)) : toDateStr(new Date()));
        setDueDate(data.dueDate ? toDateStr(new Date(data.dueDate)) : toDateStr(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)));
        setTermsAndConditions(data.termsAndConditions ?? '');
        const items = (data.lineItems ?? []).map((i: any) => ({
          id: i.id || crypto.randomUUID(),
          category: i.category ?? '',
          description: i.description ?? '',
          quantity: toNum(i.quantity),
          unit: i.unit ?? '',
          unitPriceCents: toNum(i.unitPriceCents),
          totalCents: toNum(i.totalCents),
        }));
        setLineItems(items.length ? items : [defaultLineItem()]);
      } catch (e: any) {
        setError(e.message || 'Failed to load invoice');
      } finally {
        setLoadingInvoice(false);
      }
    },
    [api]
  );

  const handleFindQuote = async () => {
    const qn = quoteNumberSearch.trim();
    if (!qn) return;
    setFindingQuote(true);
    setQuoteFindResult(null);
    setError(null);
    try {
      const quote = await api.get<any>(`/api/admin/quotes?quoteNumber=${encodeURIComponent(qn)}`);
      if (quote && (quote.id || quote.quoteNumber)) {
        setQuoteFindResult({
          id: quote.id,
          quoteNumber: quote.quoteNumber,
          homeownerName: quote.homeownerName ?? null,
          propertyAddress1: quote.propertyAddress1 ?? '',
          propertyCity: quote.propertyCity ?? '',
          propertyState: quote.propertyState ?? '',
          totalCents: quote.totalCents ?? 0,
        });
        setSelectedQuoteId(quote.id || '');
      } else {
        setQuoteFindResult(null);
        setError('Quote not found');
      }
    } catch (e: any) {
      setQuoteFindResult(null);
      setError(e.message || 'Quote not found');
    } finally {
      setFindingQuote(false);
    }
  };

  const handleGenerateDraftFromQuote = async () => {
    if (!quoteFindResult) return;
    setCreatingFromQuote(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const created = await api.post<any>('/api/admin/invoices/draft/from-quote', {
        quoteId: quoteFindResult.id,
        quoteNumber: quoteFindResult.quoteNumber,
        invoiceDate: createFromQuoteInvoiceDate,
        dueDate: createFromQuoteDueDate,
      });
      const id = created?.id ?? created?.invoice?.id;
      if (id) {
        await loadInvoice(id);
        setQuoteFindResult(null);
        setQuoteNumberSearch('');
        setSelectedQuoteId('');
        setUploadResult(null);
        setUploadError(null);
        setSuccessMessage('Draft created from quote');
      } else {
        setError('No invoice id returned from server');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to create draft from quote');
    } finally {
      setCreatingFromQuote(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setUploadError('Only PDF files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File must be under 10MB');
      return;
    }
    setUploadingPdf(true);
    setUploadError(null);
    setUploadResult(null);
    const form = new FormData();
    form.append('quotePdf', file);
    try {
      const res = await api.post<any>('/api/admin/invoices/draft/from-quote-pdf', form);
      const inv = res?.invoice ?? res;
      const id = inv?.id;
      if (id) {
        setUploadResult({
          invoice: inv,
          detected: res?.detected ?? 'external',
          quoteNumber: res?.quoteNumber,
          warnings: Array.isArray(res?.warnings) ? res.warnings : [],
        });
        setError(null);
        await loadInvoice(id);
        setSuccessMessage(res?.detected === 'corebids' ? 'Draft created from CoreBids quote PDF' : 'Draft created from external PDF; please review.');
      } else {
        setUploadError('No invoice returned from server');
      }
    } catch (e: any) {
      setUploadError(e?.message || 'Upload failed');
    } finally {
      setUploadingPdf(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveDraft = async () => {
    if (!invoiceId) return;
    if (invoice?.status !== 'DRAFT') {
      setError('Only draft invoices can be updated');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        homeownerName,
        homeownerEmail: homeownerEmail || null,
        homeownerPhone: homeownerPhone || null,
        propertyAddress1,
        propertyAddress2: propertyAddress2 || null,
        propertyCity,
        propertyState,
        propertyZip,
        contractorBusinessName: contractorBusinessName || null,
        invoiceDate,
        dueDate,
        termsAndConditions,
        lineItems: lineItems.map((r) => {
          const q = r.quantity === '' ? null : Number(r.quantity);
          const up = r.unitPriceCents === '' ? null : Number(r.unitPriceCents);
          const tot = r.totalCents === '' ? 0 : Number(r.totalCents);
          return {
            category: r.category || null,
            description: String(r.description ?? ''),
            quantity: q != null && !Number.isNaN(q) ? Math.round(q) : null,
            unit: r.unit || null,
            unitPriceCents: up != null && !Number.isNaN(up) ? Math.round(up) : null,
            totalCents: Number.isNaN(tot) ? 0 : Math.round(tot),
          };
        }),
      };
      await api.put(`/api/admin/invoices/${invoiceId}`, payload);
      await loadInvoice(invoiceId);
      setSuccessMessage('Draft saved');
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDates = async () => {
    if (!invoiceId) return;
    setUpdatingDates(true);
    setError(null);
    try {
      await api.put(`/api/admin/invoices/${invoiceId}`, { invoiceDate, dueDate });
      await loadInvoice(invoiceId);
      setSuccessMessage('Due date updated');
    } catch (e: any) {
      setError(e?.message || 'Failed to update due date');
    } finally {
      setUpdatingDates(false);
    }
  };

  const handlePreviewPdf = async () => {
    if (!invoiceId) return;
    setRenderingPdf(true);
    setError(null);
    setLastPdfUrl(null);
    const newWindow = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const renderRes = await api.post<{ pdfUrl?: string }>(`/api/admin/invoices/${invoiceId}/render`);
      const url = renderRes?.pdfUrl ?? (await api.get<{ url?: string }>(`/api/admin/invoices/${invoiceId}/pdf`))?.url;
      if (url) {
        pdfOpenedRef.current = invoiceId;
        setLastPdfUrl(url);
        if (newWindow) newWindow.location.href = url;
        else window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setError('No PDF URL returned');
        if (newWindow) newWindow.close();
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to generate PDF');
      if (newWindow) newWindow.close();
    } finally {
      setRenderingPdf(false);
    }
  };

  const handleIssue = async () => {
    if (!invoiceId) return;
    setIssuing(true);
    setError(null);
    try {
      await api.post(`/api/admin/invoices/${invoiceId}/issue`);
      await loadInvoice(invoiceId);
      setSuccessMessage('Invoice issued');
    } catch (e: any) {
      setError(e.message || 'Failed to issue');
    } finally {
      setIssuing(false);
    }
  };

  const handleChangeOrder = async () => {
    if (!invoiceId) return;
    setChangeOrdering(true);
    setError(null);
    try {
      const newInv = await api.post<any>(`/api/admin/invoices/${invoiceId}/change-order`);
      const id = newInv?.id;
      if (id) {
        await loadInvoice(id);
        setSuccessMessage('Change order draft created');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to create change order');
    } finally {
      setChangeOrdering(false);
    }
  };

  const loadDiff = useCallback(async () => {
    if (!invoiceId) return;
    setLoadingDiff(true);
    try {
      const data = await api.get<any>(`/api/admin/invoices/${invoiceId}/diff`);
      setDiffData(data);
    } catch {
      setDiffData({ hasQuote: false });
    } finally {
      setLoadingDiff(false);
    }
  }, [api, invoiceId]);

  useEffect(() => {
    if (diffOpen && invoiceId && invoice?.quoteId) loadDiff();
    else if (!diffOpen) setDiffData(null);
  }, [diffOpen, invoiceId, invoice?.quoteId, loadDiff]);

  const addLineItem = () => setLineItems((p) => [...p, defaultLineItem()]);
  const removeLineItem = (id: string) =>
    setLineItems((p) => (p.length > 1 ? p.filter((r) => r.id !== id) : p));
  const updateLineItem = (id: string, field: keyof InvoiceLineItemRow, value: string | number) => {
    setLineItems((p) => p.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const canEdit = invoice?.status === 'DRAFT';
  const canEditDates = canEdit || invoice?.status === 'ISSUED' || invoice?.status === 'VOID';
  const isIssued = invoice?.status === 'ISSUED';
  const hasQuote = Boolean(invoice?.quoteId);

  return (
    <ResponsiveContainer>
      <div style={{ padding: 24, maxWidth: 900 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>Create Invoice</h1>
        <p style={{ color: '#6b7280', marginBottom: 24 }}>
          Create an invoice from an internal quote (preferred) or upload a quote PDF.
        </p>

        {s3ConsoleUrl && (
          <p style={{ marginBottom: 16, fontSize: 14 }}>
            <a href={s3ConsoleUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
              View invoice PDFs in S3 bucket →
            </a>
          </p>
        )}

        {error && (
          <div role="alert" style={{ padding: 12, marginBottom: 16, background: '#fef2f2', color: '#b91c1c', borderRadius: 8 }}>
            {error}
          </div>
        )}
        {successMessage && (
          <div style={{ padding: 12, marginBottom: 16, background: '#f0fdf4', color: '#166534', borderRadius: 8 }}>
            {successMessage}
          </div>
        )}

        {/* Panel A: Create from Quote */}
        <SettingsCard title="Create Invoice From Quote" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 12 }}>
            <label className="block text-sm font-medium mb-1">Select a quote</label>
            <select
              value={selectedQuoteId}
              onChange={(e) => handleQuoteSelect(e.target.value)}
              disabled={loadingQuotes}
              style={{ padding: 8, minWidth: 320, maxWidth: '100%', border: '1px solid #d1d5db', borderRadius: 6 }}
            >
              <option value="">— Choose a quote —</option>
              {recentQuotes.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.quoteNumber} — {q.homeownerName || 'No name'} — {q.propertyAddress1}, {q.propertyCity}
                </option>
              ))}
            </select>
            {loadingQuotes && <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 14 }}>Loading quotes…</span>}
          </div>
          <div style={{ marginBottom: 8 }}>
            <label className="block text-sm font-medium mb-1">Or search by quote number</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="e.g. CBQ-2025-000001"
                value={quoteNumberSearch}
                onChange={(e) => setQuoteNumberSearch(e.target.value)}
                style={{ padding: 8, width: 260, border: '1px solid #d1d5db', borderRadius: 6 }}
              />
              <button type="button" className="btn btn-secondary" onClick={handleFindQuote} disabled={findingQuote}>
                {findingQuote ? 'Finding…' : 'Find'}
              </button>
            </div>
          </div>
          {quoteFindResult && (
            <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
              <div><strong>{quoteFindResult.homeownerName ?? '—'}</strong></div>
              <div>{quoteFindResult.propertyAddress1}, {quoteFindResult.propertyCity}, {quoteFindResult.propertyState}</div>
              <div>Total: ${(quoteFindResult.totalCents / 100).toFixed(2)}</div>
              <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div>
                  <label className="block text-sm font-medium mb-1">Invoice date</label>
                  <input
                    type="date"
                    value={createFromQuoteInvoiceDate}
                    onChange={(e) => setCreateFromQuoteInvoiceDate(e.target.value)}
                    style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Due date</label>
                  <input
                    type="date"
                    value={createFromQuoteDueDate}
                    onChange={(e) => setCreateFromQuoteDueDate(e.target.value)}
                    style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                  />
                </div>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                style={{ marginTop: 8 }}
                onClick={handleGenerateDraftFromQuote}
                disabled={creatingFromQuote}
              >
                {creatingFromQuote ? 'Creating…' : 'Generate Draft'}
              </button>
            </div>
          )}
        </SettingsCard>

        {/* Panel B: Upload Quote PDF */}
        <SettingsCard title="Or Upload Quote PDF" style={{ marginBottom: 24 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'block', marginBottom: 8 }}
          />
          {uploadingPdf && <p>Uploading and detecting…</p>}
          {uploadError && <p style={{ color: '#b91c1c' }}>{uploadError}</p>}
          {uploadResult && (
            <div style={{ marginTop: 12, padding: 12, background: '#f0fdf4', borderRadius: 8 }}>
              <div>Detected: <strong>{uploadResult.detected === 'corebids' ? 'CoreBids quote' : 'External PDF'}</strong></div>
              {uploadResult.quoteNumber && <div>Quote #: {uploadResult.quoteNumber}</div>}
              {uploadResult.warnings.length > 0 && (
                <ul style={{ marginTop: 8, color: '#92400e' }}>
                  {uploadResult.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>
          )}
        </SettingsCard>

        {/* Panel C: Invoice Draft Editor */}
        <SettingsCard title="Invoice Draft Editor" style={{ marginBottom: 24 }}>
          {loadingInvoice && <p>Loading invoice…</p>}
          {!invoiceId && !loadingInvoice && (
            <p style={{ color: '#6b7280' }}>Create a draft from a quote or upload a PDF above.</p>
          )}
          {invoiceId && invoice && (
            <>
              <div style={{ marginBottom: 16 }}>
                <strong>Invoice #</strong> {invoice.invoiceNumber} · Status: {invoice.status}
              </div>
              <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
                <div>
                  <label className="block text-sm font-medium mb-1">Bill To (Name)</label>
                  <input
                    type="text"
                    value={homeownerName}
                    onChange={(e) => setHomeownerName(e.target.value)}
                    disabled={!canEdit}
                    style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                  />
                </div>
                <div />
                <div>
                  <label className="block text-sm font-medium mb-1">Property Address</label>
                  <input
                    type="text"
                    value={propertyAddress1}
                    onChange={(e) => setPropertyAddress1(e.target.value)}
                    disabled={!canEdit}
                    style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">City, State, ZIP</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={propertyCity}
                      onChange={(e) => setPropertyCity(e.target.value)}
                      disabled={!canEdit}
                      placeholder="City"
                      style={{ flex: 1, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                    />
                    <input
                      type="text"
                      value={propertyState}
                      onChange={(e) => setPropertyState(e.target.value)}
                      disabled={!canEdit}
                      placeholder="State"
                      style={{ width: 60, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                    />
                    <input
                      type="text"
                      value={propertyZip}
                      onChange={(e) => setPropertyZip(e.target.value)}
                      disabled={!canEdit}
                      placeholder="ZIP"
                      style={{ width: 80, padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    disabled={!canEditDates}
                    style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Due Date</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    disabled={!canEditDates}
                    style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                  />
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <label className="block text-sm font-medium mb-1">Terms and Conditions</label>
                <textarea
                  value={termsAndConditions}
                  onChange={(e) => setTermsAndConditions(e.target.value)}
                  disabled={!canEdit}
                  rows={3}
                  style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                />
              </div>
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label className="block text-sm font-medium">Line Items</label>
                  {canEdit && <button type="button" className="btn btn-secondary" onClick={addLineItem}>Add row</button>}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: 8 }}>Description</th>
                      <th style={{ textAlign: 'right', padding: 8 }}>Qty</th>
                      <th style={{ textAlign: 'right', padding: 8 }}>Unit Price</th>
                      <th style={{ textAlign: 'right', padding: 8 }}>Amount</th>
                      {canEdit && <th style={{ width: 40 }} />}
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((row) => (
                      <tr key={row.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: 8 }}>
                          <input
                            type="text"
                            value={row.description}
                            onChange={(e) => updateLineItem(row.id, 'description', e.target.value)}
                            disabled={!canEdit}
                            style={{ width: '100%', padding: 6, border: '1px solid #e5e7eb', borderRadius: 4 }}
                          />
                        </td>
                        <td style={{ padding: 8, textAlign: 'right' }}>
                          <input
                            type="text"
                            value={row.quantity}
                            onChange={(e) => updateLineItem(row.id, 'quantity', e.target.value === '' ? '' : Number(e.target.value))}
                            disabled={!canEdit}
                            style={{ width: 56, padding: 6, textAlign: 'right', border: '1px solid #e5e7eb', borderRadius: 4 }}
                          />
                        </td>
                        <td style={{ padding: 8, textAlign: 'right' }}>
                          <input
                            type="text"
                            value={row.unitPriceCents}
                            onChange={(e) => updateLineItem(row.id, 'unitPriceCents', e.target.value === '' ? '' : Number(e.target.value))}
                            disabled={!canEdit}
                            style={{ width: 80, padding: 6, textAlign: 'right', border: '1px solid #e5e7eb', borderRadius: 4 }}
                          />
                        </td>
                        <td style={{ padding: 8, textAlign: 'right' }}>
                          <input
                            type="text"
                            value={row.totalCents}
                            onChange={(e) => updateLineItem(row.id, 'totalCents', e.target.value === '' ? '' : Number(e.target.value))}
                            disabled={!canEdit}
                            style={{ width: 80, padding: 6, textAlign: 'right', border: '1px solid #e5e7eb', borderRadius: 4 }}
                          />
                        </td>
                        {canEdit && (
                          <td style={{ padding: 8 }}>
                            <button type="button" onClick={() => removeLineItem(row.id)} style={{ padding: 4 }}>Remove</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <div>Subtotal: ${(subtotalCents / 100).toFixed(2)}</div>
                  <div>Taxes and Fees: ${(taxCents / 100).toFixed(2)}</div>
                  <div><strong>Total: ${(totalCents / 100).toFixed(2)}</strong></div>
                </div>
              </div>

              <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {canEdit && (
                  <button type="button" className="btn btn-primary" onClick={handleSaveDraft} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Draft'}
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={handlePreviewPdf} disabled={renderingPdf}>
                  {renderingPdf ? 'Rendering…' : 'Preview PDF'}
                </button>
                {canEditDates && !canEdit && (
                  <button type="button" className="btn btn-secondary" onClick={handleUpdateDates} disabled={updatingDates}>
                    {updatingDates ? 'Updating…' : 'Update due date'}
                  </button>
                )}
                {lastPdfUrl && (
                  <a
                    href={lastPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    Open PDF in new tab
                  </a>
                )}
                {canEdit && (
                  <button type="button" className="btn btn-primary" onClick={handleIssue} disabled={issuing}>
                    {issuing ? 'Issuing…' : 'Submit Invoice (Issue)'}
                  </button>
                )}
                {isIssued && (
                  <button type="button" className="btn btn-secondary" onClick={handleChangeOrder} disabled={changeOrdering}>
                    {changeOrdering ? 'Creating…' : 'Create Change Order'}
                  </button>
                )}
              </div>

              {hasQuote && (
                <div style={{ marginTop: 24 }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setDiffOpen((o) => !o)}
                  >
                    {diffOpen ? 'Hide' : 'Show'} Quote vs Invoice Diff
                  </button>
                  {diffOpen && (
                    <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
                      {loadingDiff && <p>Loading diff…</p>}
                      {diffData && !loadingDiff && (
                        <>
                          {!diffData.hasQuote && <p>{diffData.message ?? 'No linked quote'}</p>}
                          {diffData.hasQuote && (
                            <>
                              <p><strong>Quote:</strong> {diffData.quoteNumber}</p>
                              {diffData.headerChanges?.length > 0 && (
                                <div>
                                  <strong>Header changes:</strong>
                                  <ul>
                                    {diffData.headerChanges.map((c: any, i: number) => (
                                      <li key={i}>{c.field}: quote={String(c.quote)} → invoice={String(c.invoice)}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {(diffData.lineItemsAdded?.length > 0 || diffData.lineItemsRemoved?.length > 0 || diffData.lineItemsChanged?.length > 0) && (
                                <div>
                                  <strong>Line items:</strong>
                                  {diffData.lineItemsAdded?.length > 0 && <p>Added: {diffData.lineItemsAdded.length}</p>}
                                  {diffData.lineItemsRemoved?.length > 0 && <p>Removed: {diffData.lineItemsRemoved.length}</p>}
                                  {diffData.lineItemsChanged?.length > 0 && <p>Changed: {diffData.lineItemsChanged.length}</p>}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </SettingsCard>
      </div>
    </ResponsiveContainer>
  );
}
// CB:END admin-create-invoice-page
